import type { FormInstance } from 'antd';
import type { Rule } from 'antd/es/form';
import type { IntlShape } from 'react-intl';
import { mailFormat } from '@share/utils';
import { CertificatePaymentModeEnum } from '@share/common-types';

import type { PaymentFormValues, CertificatePaymentFormVariant } from './types';
import {
  CARD_CVC_MIN,
  CARD_EXPIRY_PATTERN,
  CARD_NUMBER_MIN,
  LATIN_LETTERS_PATTERN,
  MAX_LENGTH_NAME,
  MAX_LENGTH_POSTAL,
  MIN_LENGTH_LAST_NAME,
  MIN_LENGTH_PASSWORD,
  MIN_MULTICARD_SPLIT_CARD_AMOUNT_USD,
} from './constants';
import {
  minDownPaymentAmount,
  parseMoneyInput,
  roundCurrency2,
  maxDownPaymentAmount,
} from './utils/payment-math';
import { resolveCertificatePaymentMode } from './utils/resolve-payment-mode';

export interface BuildPaymentFormValidationRulesOptions {
  variant?: CertificatePaymentFormVariant;
  requiresInlineSignup?: boolean;
}

export function buildPaymentFormValidationRules(
  intl: IntlShape,
  options?: BuildPaymentFormValidationRulesOptions,
): {
  firstName: Rule[];
  lastName: Rule[];
  email: Rule[];
  nameOnCard: Rule[];
  cardNumber: Rule[];
  cardExpiry: Rule[];
  cardCvc: Rule[];
  addressLine: Rule[];
  city: Rule[];
  country: Rule[];
  postalCode: Rule[];
  agreeCharge: Rule[];
  agreeTerms: Rule[];
  password: Rule[];
  confirmPassword: Rule[];
} {
  const isPublicVariant = options?.variant === 'public';
  const requiresInlineSignup = options?.requiresInlineSignup === true;
  const requireHolderNames = !isPublicVariant || requiresInlineSignup;

  return {
    firstName: requireHolderNames
      ? [
          {
            required: true,
            message: intl.formatMessage({ id: 'certificates.payment.validation.firstNameRequired' }),
          },
          {
            max: MAX_LENGTH_NAME,
            message: intl.formatMessage(
              { id: 'certificates.payment.validation.firstNameMax' },
              { max: MAX_LENGTH_NAME },
            ),
          },
          {
            pattern: LATIN_LETTERS_PATTERN,
            message: intl.formatMessage({ id: 'certificates.payment.validation.firstNameLetters' }),
          },
        ]
      : [],
    lastName: requireHolderNames
      ? [
          {
            required: true,
            message: intl.formatMessage({ id: 'certificates.payment.validation.lastNameRequired' }),
          },
          {
            min: MIN_LENGTH_LAST_NAME,
            message: intl.formatMessage({ id: 'certificates.payment.validation.lastNameMin' }),
          },
          {
            max: MAX_LENGTH_NAME,
            message: intl.formatMessage(
              { id: 'certificates.payment.validation.lastNameMax' },
              { max: MAX_LENGTH_NAME },
            ),
          },
          {
            pattern: LATIN_LETTERS_PATTERN,
            message: intl.formatMessage({ id: 'certificates.payment.validation.lastNameLetters' }),
          },
        ]
      : [],
    email: [
      {
        required: true,
        message: intl.formatMessage({ id: 'certificates.payment.validation.emailRequired' }),
      },
      {
        pattern: new RegExp(mailFormat),
        message: intl.formatMessage({ id: 'certificates.payment.validation.emailInvalid' }),
      },
    ],
    nameOnCard: [
      {
        required: true,
        message: intl.formatMessage({ id: 'certificates.payment.validation.nameOnCardRequired' }),
      },
    ],
    cardNumber: [
      {
        message: intl.formatMessage({ id: 'certificates.payment.validation.cardNumberInvalid' }),
      },
      {
        min: CARD_NUMBER_MIN,
        message: intl.formatMessage({ id: 'certificates.payment.validation.cardNumberMin' }),
      },
    ],
    cardExpiry: [
      {
        required: true,
        message: intl.formatMessage({ id: 'certificates.payment.validation.cardExpiryRequired' }),
      },
      {
        pattern: CARD_EXPIRY_PATTERN,
        message: intl.formatMessage({ id: 'certificates.payment.validation.cardExpiryFormat' }),
      },
    ],
    cardCvc: [
      {
        required: true,
        message: intl.formatMessage({ id: 'certificates.payment.validation.cardCvcRequired' }),
      },
      {
        min: CARD_CVC_MIN,
        message: intl.formatMessage({ id: 'certificates.payment.validation.cardCvcMin' }),
      },
    ],
    addressLine: [
      {
        required: true,
        message: intl.formatMessage({ id: 'certificates.payment.validation.addressRequired' }),
      },
    ],
    city: [
      {
        required: true,
        message: intl.formatMessage({ id: 'certificates.payment.validation.cityRequired' }),
      },
      {
        pattern: LATIN_LETTERS_PATTERN,
        message: intl.formatMessage({ id: 'certificates.payment.validation.cityLetters' }),
      },
    ],
    country: [
      {
        required: true,
        message: intl.formatMessage({ id: 'certificates.payment.validation.countryRequired' }),
      },
    ],
    postalCode: [
      {
        required: true,
        message: intl.formatMessage({ id: 'certificates.payment.validation.postalRequired' }),
      },
      {
        max: MAX_LENGTH_POSTAL,
        message: intl.formatMessage(
          { id: 'certificates.payment.validation.postalMax' },
          { max: MAX_LENGTH_POSTAL },
        ),
      },
    ],
    agreeCharge: [
      {
        validator: (_: unknown, value: boolean): Promise<void> =>
          value
            ? Promise.resolve()
            : Promise.reject(
                new Error(
                  intl.formatMessage({ id: 'certificates.payment.validation.agreeCharge' }),
                ),
              ),
      },
    ],
    agreeTerms: [
      {
        validator: (_: unknown, value: boolean): Promise<void> =>
          value
            ? Promise.resolve()
            : Promise.reject(
                new Error(intl.formatMessage({ id: 'certificates.payment.validation.agreeTerms' })),
              ),
      },
    ],
    password: requiresInlineSignup
      ? [
          {
            required: true,
            message: intl.formatMessage({ id: 'certificates.payment.validation.passwordRequired' }),
          },
          {
            min: MIN_LENGTH_PASSWORD,
            message: intl.formatMessage({ id: 'password.must.be.minimum.characters' }),
          },
        ]
      : [],
    confirmPassword: requiresInlineSignup
      ? [
          {
            required: true,
            message: intl.formatMessage({
              id: 'certificates.payment.validation.confirmPasswordRequired',
            }),
          },
          ({ getFieldValue }) => ({
            validator(_: unknown, value: string): Promise<void> {
              const password = getFieldValue('password');
              if (!value || !password || value === password) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error(
                  intl.formatMessage({ id: 'certificates.payment.validation.passwordMismatch' }),
                ),
              );
            },
          }),
        ]
      : [],
  };
}

export type PaymentFormValidationRules = ReturnType<typeof buildPaymentFormValidationRules>;

function resolvedMode(form: FormInstance<PaymentFormValues>): CertificatePaymentModeEnum {
  return resolveCertificatePaymentMode(form.getFieldsValue());
}

export function createDownPaymentRules({
  intl,
  form,
  certificateTotal,
}: {
  intl: IntlShape;
  form: FormInstance<PaymentFormValues>;
  certificateTotal: number;
}): Rule[] {
  return [
    {
      validator: async (_: unknown, value: unknown): Promise<void> => {
        const mode = resolvedMode(form);
        if (
          mode !== CertificatePaymentModeEnum.Instalments &&
          mode !== CertificatePaymentModeEnum.InstalmentsSplitDownPayment
        ) {
          return;
        }
        const num = parseMoneyInput(String(value ?? ''));
        if (num === null) {
          throw new Error(
            intl.formatMessage({ id: 'certificates.payment.validation.downPaymentRequired' }),
          );
        }
        const min = minDownPaymentAmount(certificateTotal);
        if (roundCurrency2(num) < min) {
          throw new Error(
            intl.formatMessage(
              { id: 'certificates.payment.validation.downPaymentMin' },
              { min: min.toFixed(2) },
            ),
          );
        }
        const max = maxDownPaymentAmount(certificateTotal);
        if (roundCurrency2(num) > max) {
          throw new Error(
            intl.formatMessage({ id: 'certificates.payment.validation.downPaymentMax' }),
          );
        }
      },
    },
  ];
}

function createSplitPairRule({
  intl,
  form,
  targetTotal,
  modeWhenActive,
  peerField,
}: {
  intl: IntlShape;
  form: FormInstance<PaymentFormValues>;
  targetTotal: number;
  modeWhenActive: CertificatePaymentModeEnum;
  peerField: 'splitAmount1' | 'splitAmount2' | 'splitDownAmount1' | 'splitDownAmount2';
}): Rule {
  return {
    validator: async (_: unknown, value: unknown): Promise<void> => {
      if (resolvedMode(form) !== modeWhenActive) {
        return;
      }
      const self = parseMoneyInput(String(value ?? ''));
      const peer = parseMoneyInput(String(form.getFieldValue(peerField) ?? ''));
      if (self === null) {
        throw new Error(
          intl.formatMessage({ id: 'certificates.payment.validation.amountInvalid' }),
        );
      }
      if (self < MIN_MULTICARD_SPLIT_CARD_AMOUNT_USD) {
        throw new Error(
          intl.formatMessage({ id: 'certificates.payment.validation.multicardMinAmount' }),
        );
      }
      if (self > roundCurrency2(targetTotal)) {
        throw new Error(
          intl.formatMessage({ id: 'certificates.payment.validation.amountExceeds' }),
        );
      }
      if (peer === null) {
        return;
      }
      if (roundCurrency2(self + peer) !== roundCurrency2(targetTotal)) {
        throw new Error(intl.formatMessage({ id: 'certificates.payment.validation.splitSum' }));
      }
    },
  };
}

export function createFullSplitFieldRules({
  intl,
  form,
  certificateTotal,
  peerField,
}: {
  intl: IntlShape;
  form: FormInstance<PaymentFormValues>;
  certificateTotal: number;
  peerField: 'splitAmount1' | 'splitAmount2';
}): Rule[] {
  return [
    {
      required: true,
      message: intl.formatMessage({ id: 'certificates.payment.validation.amountRequired' }),
    },
    createSplitPairRule({
      intl,
      form,
      targetTotal: certificateTotal,
      modeWhenActive: CertificatePaymentModeEnum.PayInFullSplit,
      peerField,
    }),
  ];
}

export function createInstalmentDownSplitFieldRules({
  intl,
  form,
  downPaymentAmount,
  peerField,
}: {
  intl: IntlShape;
  form: FormInstance<PaymentFormValues>;
  downPaymentAmount: number;
  peerField: 'splitDownAmount1' | 'splitDownAmount2';
}): Rule[] {
  return [
    {
      required: true,
      message: intl.formatMessage({ id: 'certificates.payment.validation.amountRequired' }),
    },
    createSplitPairRule({
      intl,
      form,
      targetTotal: downPaymentAmount,
      modeWhenActive: CertificatePaymentModeEnum.InstalmentsSplitDownPayment,
      peerField,
    }),
  ];
}
